import React, { useEffect, useState } from "react";
import { Gamepad2, Trophy, Flame } from "lucide-react";
import { api } from "@/lib/api";

const MODE = { round: "10 round", survival: "Until a mistake", speed: "30 seconds" };

const Tab = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`rounded-full px-4 py-1.5 text-sm font-semibold ${on ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{children}</button>
);

const Mine = ({ rows }) => (!rows.length ? <p className="mt-4 text-sm text-muted-foreground" data-testid="game-scores-empty">Play any of the lesson games and your best scores will show here.</p> : (
  <div className="mt-4 overflow-x-auto">
    <table className="w-full text-sm" data-testid="game-scores-mine">
      <thead><tr className="text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground"><th className="py-2 pr-3">Game</th><th className="pr-3">Mode</th><th className="pr-3">All-time best</th><th>This week</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={`${r.game}-${r.mode}`} className="border-t border-border" data-testid={`game-score-${r.game}-${r.mode}`}>
          <td className="py-2 pr-3">{r.label}</td><td className="pr-3 text-muted-foreground">{r.game.startsWith("time-sig") && r.mode === "round" ? "8 round" : MODE[r.mode]}</td>
          <td className="pr-3 font-semibold text-primary">{r.best}</td><td>{r.week_best ?? "–"}</td>
        </tr>))}</tbody>
    </table>
  </div>
));

const Week = ({ boards }) => (!boards.length ? <p className="mt-4 text-sm text-muted-foreground" data-testid="game-week-empty">No scores yet this week. Be the first!</p> : (
  <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="game-scores-week">
    {boards.map((b) => (
      <div key={`${b.game}-${b.mode}`} className="rounded-2xl border border-border p-4" data-testid={`game-week-${b.game}-${b.mode}`}>
        <p className="text-sm font-semibold">{b.label}</p><p className="text-xs text-muted-foreground">{MODE[b.mode]}</p>
        <ol className="mt-2 space-y-1 text-sm">{b.top.map((t, i) => (
          <li key={i} className={`flex justify-between ${t.you ? "font-semibold text-primary" : ""}`}><span>{i + 1}. {t.name}{t.you ? " (you)" : ""}</span><span>{t.score}</span></li>))}</ol>
      </div>))}
  </div>
));

// Dashboard card: the student's own game bests, plus a 'This week' tab with every student's top weekly scores.
export const GameScoresCard = () => {
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState(null);
  const [meta, setMeta] = useState({});
  const [week, setWeek] = useState(null);
  useEffect(() => { api.get("/me/game-scores").then(({ data }) => { setMine(data.scores); setMeta(data); }).catch(() => setMine([])); }, []);
  useEffect(() => { if (tab === "week" && !week) api.get("/game-scores/week").then(({ data }) => setWeek(data.boards)).catch(() => setWeek([])); }, [tab, week]);
  if (mine === null) return null;
  return (
    <section className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="game-scores-card">
      <h2 className="flex items-center gap-2 font-serif text-2xl"><Gamepad2 size={20} className="text-primary" /> Game best scores</h2>
      <p className="mt-1 text-sm text-muted-foreground">Your best score in every lesson game, all-time and this week (Sunday to Saturday).</p>
      {meta.days_needed && (
        <p className="mt-2 text-sm" data-testid="game-streak-progress">
          <Flame size={14} className="mr-1 inline text-primary" /> Game Streak: played on <b>{Math.min(meta.days_this_week, meta.days_needed)} of {meta.days_needed}</b> days this week
          {meta.game_badges > 0 && <span className="text-primary" data-testid="game-badges"> · {meta.game_badges} {meta.game_badges === 1 ? "badge" : "badges"}</span>}
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <Tab on={tab === "mine"} onClick={() => setTab("mine")} testId="game-scores-tab-mine">My best scores</Tab>
        <Tab on={tab === "week"} onClick={() => setTab("week")} testId="game-scores-tab-week"><Trophy size={13} className="mr-1 inline" /> This week: all students</Tab>
      </div>
      {tab === "mine" ? <Mine rows={mine} /> : week === null ? <p className="mt-4 text-sm text-muted-foreground">Loading…</p> : <Week boards={week} />}
    </section>
  );
};
